import { useQueryClient } from '@tanstack/react-query';
import { useBizMutation, useBizQuery } from '../Hooks/useBizQuery';
import {
	Event,
	EventParticipator,
	GeneralEventFrontEndClientSettings,
	ParticipationStatus,
	UpdateParticipationStatusResult,
} from '../Types/event';
// import { User } from '../Types/user';
// import { hasModuleAccess } from '../Services/access';
// import { AccessTypeRight } from '../Types/access';
import { formatToSimpleDateString, transformDateStringPropsToDate } from '../Utils/dateUtils';
import { get, post } from './common';
import { useEffect, useState } from 'react';
import { GenericCustomField } from '../Types/genericCustomField';

const QUERYKEY_UPCOMING_EVENTS = 'UPCOMING_EVENTS';
const QUERYKEY_PAST_EVENTS = 'PAST_EVENTS';
const QUERYKEY_EVENTS_FOR_USER = 'EVENTS_FOR_USER';
const QUERYKEY_EVENT_PARTICIPATION = 'EVENT_PARTICIPATION';
const QUERYKEY_ONE_EVENT = 'ONE_EVENT';
const QUERYKEY_ONE_EVENT_FOR_ADMIN = 'ONE_EVENT_FOR_ADMIN';
const QUERYKEY_EVENT_PARTICIPATION_REPORT_CUSTOMFIELD_VALUES = 'EVENT_PARTICIPATION_REPORT_CUSTOMFIELD_VALUES';
const QUERYKEY_GENERAL_EVENT_FRONTEND_CLIENT_SETTINGS = 'GENERAL_EVENT_FRONTEND_CLIENT_SETTINGS';

const eventEndpoint = 'api/event';

export function eventMapper(evnt: Event | null) {
	if (!evnt) return evnt;
	return transformDateStringPropsToDate<Event, keyof Event>(evnt, ['StartDateTimeUtc', 'EndDateTimeUtc']);
}

export function eventArrayMapper(events: Event[] | null) {
	if (!events) return events;
	return events.map((e) => eventMapper(e)) as Event[];
}

//moved to property of Event populated by the backend.
// const isUserAllowedToHandleEvent = (evnt: Event, user: User) => {
// 	if (hasModuleAccess(user, 'EVENT', AccessTypeRight.EditLimited)) return true;
// 	if (!evnt.CoOrganizers) return false;
// 	return evnt.CoOrganizers.map((co) => co.Id).includes(user.Id);
// };

export const useGetUsersToCheckOffForEvent = (eventId: number) => {
	const { data } = useBizQuery([QUERYKEY_EVENT_PARTICIPATION, eventId], () => getUsersToCheckOffForEvent(eventId));
	return data ?? undefined;
};

export const useGetUpcomingEventsForUser = (enabled = true) => {
	const { data } = useBizQuery([QUERYKEY_UPCOMING_EVENTS], getUpcomingEventsForUser, enabled);
	return data ?? undefined;
};

export const useGetEventsForUser = (from: Date, to: Date) => {
	const { data } = useBizQuery([QUERYKEY_EVENTS_FOR_USER, formatToSimpleDateString(from), formatToSimpleDateString(to)], () =>
		getEventsForUser(from, to)
	);
	return data ?? undefined;
};

export const useGetPastEventsForUser = (enabled = true) => {
	const { data } = useBizQuery([QUERYKEY_PAST_EVENTS], getPastEventsForUser, enabled);
	return data ?? undefined;
};

export const useGetOneEvent = (id: number) => {
	const enabled = id > 0;
	const { data } = useBizQuery([QUERYKEY_ONE_EVENT, id], () => getOneEvent(id), enabled);
	return data;
};

export const useGetOneEventForAdmin = (id: number, deepLoad = false) => {
	const enabled = id > 0;
	const { data } = useBizQuery([QUERYKEY_ONE_EVENT_FOR_ADMIN, id, deepLoad], () => getOneEventForAdmin(id, deepLoad), enabled);
	return data ?? undefined;
};

export const useGetGeneralEventFrontEndClientSettings = () => {
	const { data } = useBizQuery([QUERYKEY_GENERAL_EVENT_FRONTEND_CLIENT_SETTINGS], getGeneralEventFrontEndClientSettings);
	return data ?? undefined;
};

export const useInvalidateOneEventCache = () => {
	const queryClient = useQueryClient();

	return {
		invalidateOneEventCache: (id: number) => queryClient.invalidateQueries({ queryKey: [QUERYKEY_ONE_EVENT_FOR_ADMIN, id] }),
	};
};

export const useUpdateTodoCompletedStatus = () => {
	const queryClient = useQueryClient();
	const [updateEventId, setUpdateEventId] = useState<number>(-1);

	const updateTodoCompletedStatusWrapperFn = (params: { eventId: number; todoId: number; completed: boolean }) => {
		setUpdateEventId(params.eventId);

		//optimistic update
		const previousEvent = queryClient.getQueryData<Event>([QUERYKEY_ONE_EVENT, params.eventId]);

		if (previousEvent) {
			const updatedTodos = previousEvent.Todos?.map((todo) =>
				todo.Id === params.todoId ? { ...todo, IsCompleted: params.completed } : todo
			);

			const updatedEvent = { ...previousEvent, Todos: updatedTodos };

			queryClient.setQueryData([QUERYKEY_ONE_EVENT, params.eventId], updatedEvent);
		}

		return updateTodoCompletedStatus(params.eventId, params.todoId, params.completed);
	};

	const { mutate, statusState } = useBizMutation(updateTodoCompletedStatusWrapperFn);

	useEffect(() => {
		if (statusState === 'SUCCESS') {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_ONE_EVENT, updateEventId] });
		}
	}, [statusState]);

	return {
		updateTodoCompletedStatus: (eventId: number, todoId: number, completed: boolean) =>
			mutate({ eventId, todoId, completed }),
		statusState,
	};
};

export const useUpdateParticipationStatus = (customQueryKey?: string, disableOptimisticUpdate = false) => {
	const queryClient = useQueryClient();
	const [updateEventId, setUpdateEventId] = useState<number>(-1);

	const updateParticipationStatusWrapperFn = (params: { eventId: number; status: ParticipationStatus; comment: string }) => {
		setUpdateEventId(params.eventId);

		const performOptimisticUpdate = (queryKey: string) => {
			if (disableOptimisticUpdate) return;
			//optimistic update
			const previousEvent = queryClient.getQueryData<Event>([queryKey, params.eventId]);

			if (previousEvent) {
				const updatedEvent = { ...previousEvent, UserStatus: params.status };
				queryClient.setQueryData([queryKey, params.eventId], updatedEvent);
			}
		};

		performOptimisticUpdate(QUERYKEY_ONE_EVENT);
		if (customQueryKey) {
			performOptimisticUpdate(customQueryKey);
		}

		return updateParticipationStatus(params.eventId, params.status, params.comment);
	};

	const { mutate, statusState, data } = useBizMutation(updateParticipationStatusWrapperFn);

	useEffect(() => {
		if (statusState === 'SUCCESS') {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_ONE_EVENT, updateEventId] });
			if (customQueryKey) {
				queryClient.invalidateQueries({ queryKey: [customQueryKey, updateEventId] });
			}
		}
	}, [statusState]);

	return {
		updateParticipationStatus: (eventId: number, status: ParticipationStatus, comment?: string) =>
			mutate({ eventId, status, comment: comment ?? '' }),
		statusState,
		updateParticipationStatusResult: data?.ExtraInfo,
	};
};

export const useToggleParticipationStatusForRelatedUserGroup = (customQueryKey?: string) => {
	const toggleParticipationStatusForRelatedUserGroupWrapperFn = (params: { eventId: number; status: ParticipationStatus }) => {
		return toggleParticipationStatusForRelatedUserGroup(params.eventId, params.status);
	};

	const { mutate, statusState } = useBizMutation(toggleParticipationStatusForRelatedUserGroupWrapperFn, {
		getQueryKeyForInvalidation: (variables) => {
			const keys = [[QUERYKEY_ONE_EVENT, variables.eventId]];
			if (customQueryKey) {
				keys.push([customQueryKey, variables.eventId]);
			}
			return keys;
		},
	});

	return {
		toggleParticipationStatusForRelatedUserGroup: (eventId: number, status: ParticipationStatus) =>
			mutate({ eventId, status }),
		statusState,
	};
};

export const useToggleParticipationStatusForUserOrRelatedUser = (customQueryKey?: string) => {
	const queryClient = useQueryClient();

	const updateParticipationStatusWrapperFn = (params: {
		eventId: number;
		userId: number;
		status: ParticipationStatus;
		comment: string;
	}) => {
		const performOptimisticUpdate = (queryKey: string) => {
			const previousEvent = queryClient.getQueryData<Event>([queryKey, params.eventId]);

			if (previousEvent) {
				const newRelatedParticipationUserIds = [...(previousEvent.RelatedParticipationUserIds ?? [])];
				if (params.status === ParticipationStatus.Attending) {
					if (!newRelatedParticipationUserIds.includes(params.userId)) {
						newRelatedParticipationUserIds.push(params.userId);
					}
				} else {
					const index = newRelatedParticipationUserIds.indexOf(params.userId);
					if (index > -1) {
						newRelatedParticipationUserIds.splice(index, 1);
					}
				}
				const updatedEvent = { ...previousEvent, RelatedParticipationUserIds: newRelatedParticipationUserIds };
				queryClient.setQueryData([queryKey, params.eventId], updatedEvent);
			}
		};

		performOptimisticUpdate(QUERYKEY_ONE_EVENT);
		if (customQueryKey) {
			performOptimisticUpdate(customQueryKey);
		}

		return toggleParticipationStatusForUserOrRelatedUser(params.eventId, params.userId, params.status);
	};

	const { mutate, statusState } = useBizMutation(updateParticipationStatusWrapperFn, {
		getQueryKeyForInvalidation: (variables) => {
			const keys = [[QUERYKEY_ONE_EVENT, variables.eventId]];
			if (customQueryKey) {
				keys.push([customQueryKey, variables.eventId]);
			}
			return keys;
		},
	});

	return {
		toggleParticipationStatusForUserOrRelatedUser: (eventId: number, userId: number, status: ParticipationStatus) =>
			mutate({ eventId, userId, status, comment: '' }),
		statusState,
	};
};

export const useUpdateConfirmedAttending = () => {
	const queryClient = useQueryClient();
	const [updateEventId, setUpdateEventId] = useState<number>(-1);

	const updateConfirmedAttendingWrapperFn = (params: {
		eventId: number;
		confirmedAttendedUserIds: number[];
		internalComment: string | null;
		completeEvent: boolean;
		participationReportCustomFields: GenericCustomField[] | null;
	}) => {
		setUpdateEventId(params.eventId);

		return updateConfirmedAttending(
			params.eventId,
			params.confirmedAttendedUserIds,
			params.internalComment,
			params.completeEvent,
			params.participationReportCustomFields
		);
	};

	const { mutate, statusState } = useBizMutation(updateConfirmedAttendingWrapperFn);

	useEffect(() => {
		if (statusState === 'SUCCESS') {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_EVENT_PARTICIPATION, updateEventId] });
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_EVENT_PARTICIPATION_REPORT_CUSTOMFIELD_VALUES, updateEventId] });
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_ONE_EVENT, updateEventId] });
		}
	}, [statusState]);

	return {
		updateConfirmedAttending: (
			eventId: number,
			confirmedAttendedUserIds: number[],
			internalComment: string | null,
			completeEvent: boolean,
			participationReportCustomFields: GenericCustomField[] | null
		) => mutate({ eventId, confirmedAttendedUserIds, internalComment, completeEvent, participationReportCustomFields }),
		statusState,
	};
};

export const useCompleteEvent = () => {
	const mutationFn = (params: { eventId: number }) => {
		return completeEvent(params.eventId);
	};

	const { mutate, statusState } = useBizMutation(mutationFn, {
		getQueryKeyForInvalidation: (variables) => [
			[QUERYKEY_ONE_EVENT, variables.eventId],
			[QUERYKEY_ONE_EVENT_FOR_ADMIN, variables.eventId],
			[QUERYKEY_EVENTS_FOR_USER],
			[QUERYKEY_UPCOMING_EVENTS],
		],
	});

	return {
		completeEvent: (eventId: number) => mutate({ eventId }),
		statusState,
	};
};

export const useGetParticipationReportCustomFieldValues = (eventId: number) => {
	const { data } = useBizQuery([QUERYKEY_EVENT_PARTICIPATION_REPORT_CUSTOMFIELD_VALUES, eventId], () =>
		getParticipationReportCustomFieldValues(eventId)
	);
	return data;
};

export const useDeleteParticipationForUser = () => {
	const { mutate, statusState, variables, errorMessage } = useBizMutation(
		({ eventId, userId }: { eventId: number; userId: number }) =>
			post(`${eventEndpoint}/DeleteParticipationForUser?eventId=${eventId}&userId=${userId}`, {}),
		{
			getQueryKeyForInvalidation: ({ eventId }) => [
				[QUERYKEY_EVENT_PARTICIPATION, eventId],
				[QUERYKEY_ONE_EVENT, eventId],
				[QUERYKEY_ONE_EVENT_FOR_ADMIN, eventId],
				[QUERYKEY_EVENTS_FOR_USER],
				[QUERYKEY_UPCOMING_EVENTS],
				[QUERYKEY_PAST_EVENTS],
			],
		}
	);
	return {
		deleteParticipationForUser: (eventId: number, userId: number) => mutate({ eventId, userId }),
		statusState,
		variables,
		errorMessage,
	};
};

export const useUpdateParticipationStatusForUser = () => {
	const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);

	const mutationFn = (params: { eventId: number; userId: number; status: ParticipationStatus }) => {
		setUpdatingUserId(params.userId);
		return updateParticipationStatusForUser(params.eventId, params.userId, params.status);
	};

	const { mutate, statusState } = useBizMutation(mutationFn, {
		getQueryKeyForInvalidation: (variables) => [[QUERYKEY_EVENT_PARTICIPATION, variables.eventId]],
	});

	useEffect(() => {
		if (statusState === 'SUCCESS' || statusState === 'ERROR') {
			setUpdatingUserId(null);
		}
	}, [statusState]);

	return {
		updateParticipationStatusForUser: (eventId: number, userId: number, status: ParticipationStatus) =>
			mutate({ eventId, userId, status }),
		statusState,
		updatingUserId,
	};
};

export const useSelfAddAsSubstituteCoOrganizer = () => {
	const mutationFn = (params: { eventId: number }) => selfAddAsSubstituteCoOrganizer(params.eventId);

	const { mutate, statusState, errorType } = useBizMutation(mutationFn, {
		getQueryKeyForInvalidation: (variables) => [
			[QUERYKEY_ONE_EVENT, variables.eventId],
			[QUERYKEY_ONE_EVENT_FOR_ADMIN, variables.eventId],
			[QUERYKEY_EVENT_PARTICIPATION, variables.eventId],
		],
	});

	return {
		selfAddAsSubstituteCoOrganizer: (eventId: number) => mutate({ eventId }),
		statusState,
		errorType,
	};
};

export const useSelfRegisterAbsenceAsCoOrganizer = () => {
	const mutationFn = (params: { eventId: number; reason: string; absenceType: string }) =>
		selfRegisterAbsenceAsCoOrganizer(params.eventId, params.reason, params.absenceType);

	const { mutate, statusState } = useBizMutation(mutationFn, {
		getQueryKeyForInvalidation: (variables) => [
			[QUERYKEY_ONE_EVENT, variables.eventId],
			[QUERYKEY_ONE_EVENT_FOR_ADMIN, variables.eventId],
			[QUERYKEY_EVENT_PARTICIPATION, variables.eventId],
		],
	});

	return {
		selfRegisterAbsenceAsCoOrganizer: (eventId: number, reason: string, absenceType: string) =>
			mutate({ eventId, reason, absenceType }),
		statusState,
	};
};

async function getUpcomingEventsForUser() {
	const endpoint = `${eventEndpoint}/GetUpcomingEventsForUser`;
	const result = await get<Event[]>(endpoint);
	return eventArrayMapper(result);
}

async function getPastEventsForUser() {
	const minDate = new Date();
	minDate.setFullYear(minDate.getFullYear() - 10);
	const now = new Date();

	const endpoint = `${eventEndpoint}/GetEventsForUser?from=${formatToSimpleDateString(minDate)}&to=${formatToSimpleDateString(now)}`;
	const result = await get<Event[]>(endpoint);
	return eventArrayMapper(result);
}

async function getOneEvent(id: number) {
	const endpoint = `${eventEndpoint}/GetOneEventForUser?eventId=${id}`;
	const result = await get<Event | null>(endpoint);
	return eventMapper(result);
}

async function getGeneralEventFrontEndClientSettings() {
	const endpoint = `${eventEndpoint}/GetGeneralEventFrontEndClientSettings`;
	return await get<GeneralEventFrontEndClientSettings>(endpoint);
}

async function updateTodoCompletedStatus(eventId: number, todoId: number, completed: boolean) {
	const endpoint = `${eventEndpoint}/UpdateTodoCompletedStatus?eventId=${eventId}&todoId=${todoId}&completed=${completed}`;
	await post(endpoint);
}

async function updateParticipationStatus(eventId: number, status: ParticipationStatus, comment: string) {
	const endpoint = `${eventEndpoint}/UpdateParticipationStatus`;
	const response = await post<{ Success: boolean; ExtraInfo: UpdateParticipationStatusResult | null }>(endpoint, {
		EventId: eventId,
		Status: status,
		Comment: comment,
	});
	return response;
}

async function toggleParticipationStatusForRelatedUserGroup(eventId: number, status: ParticipationStatus) {
	const endpoint = `${eventEndpoint}/ToggleParticipationStatusForRelatedUserGroup`;
	const response = await post<{ Success: boolean; ExtraInfo: UpdateParticipationStatusResult | null }>(endpoint, {
		EventId: eventId,
		Status: status,
	});
	return response;
}

async function toggleParticipationStatusForUserOrRelatedUser(eventId: number, userId: number, status: ParticipationStatus) {
	const endpoint = `${eventEndpoint}/ToggleParticipationStatusForUserOrRelatedUser`;
	const response = await post<{ Success: boolean; ExtraInfo: UpdateParticipationStatusResult | null }>(endpoint, {
		EventId: eventId,
		RelatedGroupParticipantUserIds: [userId],
		Status: status,
	});
	return response;
}

async function getUsersToCheckOffForEvent(eventId: number) {
	const endpoint = `${eventEndpoint}/GetUsersToCheckOffForEvent?eventId=${eventId}`;
	const result = await get<EventParticipator[]>(endpoint);
	return result;
}

async function updateConfirmedAttending(
	eventId: number,
	confirmedAttendedUserIds: number[],
	internalComment: string | null,
	completeEvent: boolean,
	participationReportCustomFields: GenericCustomField[] | null
) {
	const endpoint = `${eventEndpoint}/UpdateConfirmedAttending`;
	const response = await post(endpoint, {
		EventId: eventId,
		ConfirmedAttendedUserIds: confirmedAttendedUserIds,
		Comment: internalComment,
		CompleteEvent: completeEvent,
		ParticipationReportCustomFields: participationReportCustomFields,
	});
	return response;
}

async function getParticipationReportCustomFieldValues(eventId: number) {
	const endpoint = `${eventEndpoint}/GetParticipationReportCustomFieldValues?eventId=${eventId}`;
	const result = await get<GenericCustomField[]>(endpoint);
	return result;
}

async function selfAddAsSubstituteCoOrganizer(eventId: number) {
	const endpoint = `${eventEndpoint}/SelfAddAsSubstituteCoOrganizer?eventId=${eventId}`;
	await post(endpoint);
}

async function selfRegisterAbsenceAsCoOrganizer(eventId: number, reason: string, absenceType: string) {
	const endpoint = `${eventEndpoint}/SelfRegisterAbsenceAsCoOrganizer`;
	await post(endpoint, { EventId: eventId, Reason: reason, AbsenceType: absenceType });
}

async function updateParticipationStatusForUser(eventId: number, userId: number, status: ParticipationStatus) {
	const endpoint = `${eventEndpoint}/UpdateParticipationStatusForUser`;
	await post(endpoint, { EventId: eventId, UserId: userId, Status: status });
}

async function getEventsForUser(from: Date, to: Date) {
	const endpoint = `${eventEndpoint}/GetEventsForUser?from=${formatToSimpleDateString(from)}&to=${formatToSimpleDateString(to)}`;
	const result = await get<Event[]>(endpoint);
	return eventArrayMapper(result);
}

async function completeEvent(eventId: number) {
	const endpoint = `${eventEndpoint}/CompleteEvent?eventId=${eventId}`;
	await post(endpoint);
}

async function getOneEventForAdmin(id: number, deepLoad = false) {
	const endpoint = `${eventEndpoint}/GetOneEventForAdmin?eventId=${id}&deepLoad=${deepLoad}`;
	const result = await get<Event>(endpoint);
	return eventMapper(result);
}
